/**
 * ClientReferrals Component
 * Shows referrals from clients for Advisors and Managers
 */
import React, { useState, useEffect } from 'react';
import { 
  FaUserPlus, FaSpinner, FaPhone, FaBriefcase, FaUser, 
  FaCheck, FaTimes, FaPhoneAlt, FaChevronDown, FaChevronUp,
  FaGift, FaClock, FaCheckCircle, FaTimesCircle
} from 'react-icons/fa';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  contacted: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  converted: 'bg-green-500/20 text-green-400 border-green-500/30',
  rejected: 'bg-red-500/20 text-red-400 border-red-500/30'
};

const statusIcons = {
  pending: FaClock,
  contacted: FaPhoneAlt,
  converted: FaCheckCircle,
  rejected: FaTimesCircle
};

const ClientReferrals = () => {
  const [referrals, setReferrals] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, contacted: 0, converted: 0 });
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [updating, setUpdating] = useState(null);

  const getToken = () => localStorage.getItem('token');
  const headers = () => ({ Authorization: `Bearer ${getToken()}` });

  useEffect(() => {
    fetchReferrals();
  }, []);

  const fetchReferrals = async () => {
    try {
      const response = await axios.get(`${API}/advisor/referrals`, { headers: headers() });
      setReferrals(response.data.referrals || []);
      setStats(response.data.stats || { total: 0, pending: 0, contacted: 0, converted: 0 });
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (referralId, newStatus) => {
    setUpdating(referralId);
    try {
      await axios.put(`${API}/advisor/referrals/${referralId}/status`, { status: newStatus }, { headers: headers() });
      await fetchReferrals();
    } catch (error) {
    } finally {
      setUpdating(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="bg-[#0F172A] rounded-xl p-6 border border-[#2D5A8F]">
        <div className="flex items-center justify-center py-8">
          <FaSpinner className="animate-spin text-[#FFD700] text-2xl" />
        </div>
      </div>
    );
  }

  if (referrals.length === 0) {
    return null; // Don't render if no referrals
  }

  return (
    <div className="bg-[#0F172A] rounded-xl p-6 border border-[#2D5A8F]">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center">
            <FaUserPlus className="text-white" />
          </div>
          <div>
            <h3 className="text-white font-bold text-lg">Client Referrals</h3>
            <p className="text-gray-400 text-sm">Leads from your clients</p>
          </div>
        </div>
        <span className="px-3 py-1 bg-[#FFD700]/20 text-[#FFD700] font-bold rounded-full text-sm">
          {stats.total} Total
        </span>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-yellow-500/10 rounded-lg p-3 text-center">
          <p className="text-yellow-400 font-bold text-xl">{stats.pending}</p>
          <p className="text-gray-400 text-xs">Pending</p>
        </div>
        <div className="bg-blue-500/10 rounded-lg p-3 text-center">
          <p className="text-blue-400 font-bold text-xl">{stats.contacted}</p>
          <p className="text-gray-400 text-xs">Contacted</p>
        </div>
        <div className="bg-green-500/10 rounded-lg p-3 text-center">
          <p className="text-green-400 font-bold text-xl">{stats.converted}</p>
          <p className="text-gray-400 text-xs">Converted</p>
        </div>
        <div className="bg-[#FFD700]/10 rounded-lg p-3 text-center">
          <p className="text-[#FFD700] font-bold text-xl">₹{stats.converted * 300}</p>
          <p className="text-gray-400 text-xs">Rewards Due</p>
        </div>
      </div>

      {/* Referrals List */}
      <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
        {referrals.map((referral) => {
          const StatusIcon = statusIcons[referral.status] || FaClock;
          const isExpanded = expandedId === referral.id;
          
          return (
            <div 
              key={referral.id}
              className="bg-[#1A2942] rounded-lg border border-[#2D5A8F] overflow-hidden"
            >
              {/* Main Row */}
              <div 
                className="p-4 cursor-pointer hover:bg-[#1E3A5F]/50 transition-all"
                onClick={() => setExpandedId(isExpanded ? null : referral.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 rounded-full bg-[#2D5A8F] flex items-center justify-center">
                      <FaUser className="text-gray-400" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">{referral.friend_name}</p>
                      <p className="text-gray-400 text-sm flex items-center space-x-2">
                        <FaPhone size={10} />
                        <span>{referral.friend_contact}</span>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${statusColors[referral.status]}`}>
                      <StatusIcon className="inline mr-1" size={10} />
                      {referral.status.charAt(0).toUpperCase() + referral.status.slice(1)}
                    </span>
                    {isExpanded ? <FaChevronUp className="text-gray-400" /> : <FaChevronDown className="text-gray-400" />}
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div className="px-4 pb-4 border-t border-[#2D5A8F] pt-3">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-gray-500 text-xs">Profile</p>
                      <p className="text-gray-300 text-sm flex items-center">
                        <FaBriefcase className="mr-2 text-[#FFD700]" size={12} />
                        {referral.friend_profile || 'Not specified'}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs">Referred By</p>
                      <p className="text-gray-300 text-sm">{referral.referrer_name}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs">Referred On</p>
                      <p className="text-gray-300 text-sm">{formatDate(referral.created_at)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs">Reward</p>
                      <p className="text-[#FFD700] text-sm flex items-center">
                        <FaGift className="mr-1" size={12} />
                        ₹{referral.reward_amount}
                      </p>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-2">
                    {referral.status === 'pending' && (
                      <>
                        <button
                          onClick={() => updateStatus(referral.id, 'contacted')}
                          disabled={updating === referral.id}
                          className="px-3 py-1.5 bg-blue-500/20 text-blue-400 rounded-lg text-xs font-medium hover:bg-blue-500/30 transition-all flex items-center space-x-1 disabled:opacity-50"
                        >
                          <FaPhoneAlt size={10} />
                          <span>Mark Contacted</span>
                        </button>
                        <button
                          onClick={() => updateStatus(referral.id, 'rejected')}
                          disabled={updating === referral.id}
                          className="px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg text-xs font-medium hover:bg-red-500/30 transition-all flex items-center space-x-1 disabled:opacity-50"
                        >
                          <FaTimes size={10} />
                          <span>Reject</span>
                        </button>
                      </>
                    )}
                    {referral.status === 'contacted' && (
                      <>
                        <button
                          onClick={() => updateStatus(referral.id, 'converted')}
                          disabled={updating === referral.id}
                          className="px-3 py-1.5 bg-green-500/20 text-green-400 rounded-lg text-xs font-medium hover:bg-green-500/30 transition-all flex items-center space-x-1 disabled:opacity-50"
                        >
                          <FaCheck size={10} />
                          <span>Mark Converted</span>
                        </button>
                        <button
                          onClick={() => updateStatus(referral.id, 'rejected')}
                          disabled={updating === referral.id}
                          className="px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg text-xs font-medium hover:bg-red-500/30 transition-all flex items-center space-x-1 disabled:opacity-50"
                        >
                          <FaTimes size={10} />
                          <span>Reject</span>
                        </button>
                      </>
                    )}
                    {updating === referral.id && (
                      <FaSpinner className="animate-spin text-[#FFD700]" />
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ClientReferrals;
